import { Stack, Tooltip, Typography } from "@mui/material";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import { audienceLabel } from "@/admin/entities/assistant";
import { topicLabel } from "./topicLabel";
import type { AssistantConversationRow } from "./types";

function renderUser(row: AssistantConversationRow) {
  if (!row.user) return "-";
  return (
    <CrudLink path={PagePaths.users} tab="details" id={row.user.id}>
      {row.user.displayName || row.user.email || row.user.id}
    </CrudLink>
  );
}

function renderPlatform(row: AssistantConversationRow) {
  if (!row.platform) return "-";
  return (
    <CrudLink path={PagePaths.platforms} tab="details" id={row.platform.id}>
      {row.platform.title}
    </CrudLink>
  );
}

function renderReactions(row: AssistantConversationRow) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Stack direction="row" spacing={0.5} alignItems="center">
        <ThumbUpAltOutlinedIcon fontSize="small" color="action" />
        <Typography variant="body2">{row.likes}</Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <ThumbDownAltOutlinedIcon fontSize="small" color="action" />
        <Typography variant="body2">{row.dislikes}</Typography>
      </Stack>
    </Stack>
  );
}

export const getColumnsConfig =
  (): ColumnDefinition<AssistantConversationRow>[] => [
    { name: "id", label: "ID", options: { display: false, filter: false } },
    {
      name: "audience",
      label: "Аудитория",
      options: {
        sort: false,
        customBodyRender: (value: unknown) =>
          audienceLabel(value as string | null),
      },
    },
    {
      name: "platform",
      label: "Арена",
      options: {
        sort: false,
        customBodyRender: (data: unknown) =>
          renderPlatform(data as AssistantConversationRow),
      },
    },
    {
      name: "user",
      label: "Пользователь",
      options: {
        sort: false,
        customBodyRender: (data: unknown) =>
          renderUser(data as AssistantConversationRow),
      },
    },
    {
      name: "title",
      label: "Заголовок",
      options: {
        sort: false,
        customBodyRender: (data: unknown) => {
          const row = data as AssistantConversationRow;
          return (
            <CrudLink id={row.id} tab="details">
              {row.title || "Без заголовка"}
            </CrudLink>
          );
        },
      },
    },
    {
      name: "topic",
      label: "Тема",
      options: {
        sort: false,
        customBodyRender: (value: unknown) => {
          const topic = value as string | null;
          if (!topic) return "-";
          return topicLabel[topic] ?? topic;
        },
      },
    },
    {
      name: "reactions",
      label: "👍/👎",
      options: {
        sort: false,
        customBodyRender: (data: unknown) =>
          renderReactions(data as AssistantConversationRow),
      },
    },
    {
      name: "knowledgeGap",
      label: "Не знал",
      options: {
        sort: false,
        customBodyRender: (value: unknown) =>
          value ? (
            <Tooltip title="Помощник не нашёл ответ в базе знаний">
              <HelpOutlineOutlinedIcon fontSize="small" color="warning" />
            </Tooltip>
          ) : null,
      },
    },
    {
      name: "hasRequest",
      label: "Обращение",
      options: {
        sort: false,
        customBodyRender: (value: unknown) =>
          value ? (
            <Tooltip title="По диалогу создано обращение в поддержку">
              <SupportAgentOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          ) : null,
      },
    },
    {
      name: "costUsd",
      label: "Стоимость",
      options: {
        sort: false,
        customBodyRender: (value: unknown) =>
          `$${((value as number) ?? 0).toFixed(3)}`,
      },
    },
    {
      name: "lastMessageAt",
      label: "Последнее сообщение",
      options: {
        setCellHeaderProps: () => ({ style: { width: 170 } }),
        customBodyRender: (value: unknown) => (
          <CustomCellDate date={value as string} />
        ),
      },
    },
  ];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Диалоги",
  refetchList: "Обновить список",
  showTitle: "Диалог",
  editItem: "Диалог",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<AssistantConversationRow> => ({
    main: {
      title: "Диалог",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <CrudLink id={row.id} tab="details" noWrap>
            {row.title || "Без заголовка"}
          </CrudLink>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.platform?.title ?? "-"}
          </Typography>
        </Stack>
      ),
    },
    cost: {
      title: "Стоимость",
      sx: { width: 96, flexShrink: 0 },
      renderCell: (row) => `$${(row.costUsd ?? 0).toFixed(3)}`,
    },
  });
